/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable consistent-return */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Row, Col } from 'antd';
import { useParams } from 'react-router-dom';

import api from '@api';
import { Block, Checkbox, ErrorMessage } from '@atoms';
import {
  ArticleLanguageSelects,
  AvailableLanguages,
  CampaignField,
  CheckboxField,
  TitleField,
  EventField,
  ColorField,
  SlugField,
  TextField,
  ArticleChampionshipCheckbox,
} from '@molecules';
import MediaField from '@organisms/MediaField';
import HideOnMainSiteCheck from '@molecules/HideOnMainSite';
import RichTextField from '@molecules/RichTextField';
import { NO_MARGIN_BOTTOM } from '@constants';
import ROUTES from '@constants/routes';
import { LIVE_BLOG } from '@constants/contentModules';
import { showConfirm } from '@utils/modal';
import { useIsLOCadmin, useWatchFormValues, useEventColorsAtom, useLOCevent } from '@hooks';
import { useEvent } from '@api/hooks';
import { transformEventChampionships } from '@api/utils';
import useEventColors from '@hooks/useEventColors';

import ContentModulesField from '../ContentModulesField';
import Form from '../Form';
import RelatedContentField from '../RelatedContentField';

import ArticleTypeField from './ArticleTypeField';
import ArticleLayoutField from './ArticleLayoutField';
import { FORM_FIELD, FORM_FIELDS, DEFAULT_VALUES, FORM_VALUES_TO_WATCH } from './constants';

const MainBlock = ({
  selectLanguageReloadContent,
  showLanguageSelects,
  availableLanguages,
  setTranslateError,
  setCheckedEvent,
  isArticleCreate,
  initialValues,
  onEventChange,
  eventId,
  locale,
  form,
}) => {
  const isLOCadmin = useIsLOCadmin();
  const [linkEvent, setLinkEvent] = React.useState(initialValues.event);
  const eventColors = useEventColors();
  const { event: articleEvent } = useEvent(eventId);

  const onSetLinkEvent = (event) => {
    const { checked } = event.target;
    setCheckedEvent(checked);
    if (!checked) {
      form.setFields([{ name: FORM_FIELD.EVENT.name, errors: [] }]);
      form.setFieldsValue([{ name: FORM_FIELD.CHAMPIONSHIP_ARTICLE.name, value: false, errors: [] }]);
    }
    // An ugly hack to make clear error if disabling field.
    // Seems to happen because of form store is working on subscriptions - field is getting disabled before it can clear error
    // So we disable it on next tick
    setTimeout(() => setLinkEvent(checked));
  };

  const availableLanguageListArray = availableLanguages && availableLanguages.map((item) => item.code);
  const availableLanguageList = availableLanguageListArray || [];

  return (
    <Block spaced>
      <Row gutter={16}>
        <Col span={17}>
          <ArticleTypeField {...FORM_FIELD.TYPE} wrapperCol={{ span: 20 }} />
        </Col>
        <Col span={7}>
          {showLanguageSelects ? (
            <>
              <ArticleLanguageSelects
                selectLanguageReloadContent={selectLanguageReloadContent}
                availableLanguages={availableLanguages}
                setTranslateError={setTranslateError}
                field={FORM_FIELD.ARTICLE_LANGUAGE}
                disabled={isArticleCreate}
                eventId={eventId}
                locale={locale}
                form={form}
              />
              <AvailableLanguages entity={{ availableLanguages: availableLanguageList }} />
            </>
          ) : (
            <>
              {/* we need the field to be rendered in order to send data */}
              <Form.Item name={FORM_FIELD.ARTICLE_LANGUAGE.name} style={{ display: 'none' }} />
              <AvailableLanguages entity={{ availableLanguages: availableLanguageList }} />
            </>
          )}
        </Col>
      </Row>

      <ArticleLayoutField {...FORM_FIELD.LAYOUT} />
      <ColorField {...FORM_FIELD.BACKGROUND_COLOUR} />
      <ColorField {...FORM_FIELD.RELATED_ARTICLES_BACKGROUND_COLOUR} mergeColors={eventColors} />
      <Row gutter={15}>
        <Col>
          <Checkbox checked={linkEvent} onChange={onSetLinkEvent} disabled={isLOCadmin} />
        </Col>
        <Col flex={1}>
          <EventField
            onChange={(e) => onEventChange(e, 'linkedEvent')}
            {...(linkEvent ? FORM_FIELD.EVENT : null)}
            disabled={!linkEvent || isLOCadmin}
            label={FORM_FIELD.EVENT.label}
            wrapperCol={{ span: 24 }}
            extra={null}
          />
        </Col>
      </Row>
      {articleEvent?.categoryCode === 'WAS' && linkEvent && (
        <ArticleChampionshipCheckbox
          {...FORM_FIELD.CHAMPIONSHIP_ARTICLE}
          labelClassName="ant-form-item-label"
          labelStyle={{ fontSize: '18px' }}
          style={{ ...NO_MARGIN_BOTTOM, ...(isLOCadmin && { visibility: 'hidden', height: 0 }) }}
        />
      )}
      <HideOnMainSiteCheck
        checkButton={!initialValues[FORM_FIELD.RELATED_ARTICLES.name]}
        minisiteFormField={FORM_FIELD.RELATED_MINISITES.name}
        labelClassName="ant-form-item-label"
        {...FORM_FIELD.HIDE_ON_MAIN_SITE}
        labelStyle={{ fontSize: '18px' }}
        style={NO_MARGIN_BOTTOM}
      />
    </Block>
  );
};

const SeoBlock = ({ initialValues, isGated }) => {
  const isLOCadmin = useIsLOCadmin();
  return (
    <Block spaced>
      <TitleField {...FORM_FIELD.TITLE} extra={null} wrapperCol={{ span: 24 }} />
      <SlugField
        {...FORM_FIELD.SLUG}
        generateSlugFrom={!initialValues[FORM_FIELD.SLUG.name] && FORM_FIELD.TITLE.name}
      />
      <MediaField {...FORM_FIELD.FEATURE_IMAGE} />
      <TextField multiline {...FORM_FIELD.STANDFIRST} />
      <RichTextField {...FORM_FIELD.BODY} />
      <TextField {...FORM_FIELD.SEO_TITLE} />
      <TextField multiline {...FORM_FIELD.SEO_DESCRIPTION} style={isLOCadmin && NO_MARGIN_BOTTOM} />
      <TextField {...FORM_FIELD.RELATED_CHAMPIONSHIP_EVENTS} style={{ display: 'none' }} />
      {!isLOCadmin && (
        <>
          <CheckboxField {...FORM_FIELD.GATED_CONTENT} style={!isGated && NO_MARGIN_BOTTOM} />
          {isGated && <CampaignField {...FORM_FIELD.CAMPAIGN} style={NO_MARGIN_BOTTOM} />}
        </>
      )}
    </Block>
  );
};

const RelatedContentBlock = ({ form, eventDisciplines, isEventFetching, onEventChange }) => (
  <Block spaced>
    <RelatedContentField
      onRelatedEventChange={onEventChange}
      eventDisciplines={eventDisciplines}
      isEventFetching={isEventFetching}
      FORM_FIELD={FORM_FIELD}
      fields={FORM_FIELDS}
      form={form}
    />
  </Block>
);

const ContentModuleBlock = ({ form, articleId, saveDraft, onEventDelete }) => {
  const navigate = useNavigate();

  const handleAllowAdd = React.useCallback(
    ({ type }) => {
      if (articleId || type !== LIVE_BLOG) return true;

      return new Promise((resolve) => {
        showConfirm({
          title: 'Before adding live blog please save the article',
          okText: 'SAVE DRAFT',
          onOk: () =>
            saveDraft(null, {
              onSuccess: (article) => {
                resolve(false);
                navigate(`${ROUTES.ARTICLES}/${article.id}?contentModule=${LIVE_BLOG}`);
              },
            }),
          onCancel: () => resolve(false),
        });
      });
    },
    [navigate, articleId, saveDraft],
  );

  return (
    <ContentModulesField
      {...FORM_FIELD.CONTENT_MODULES}
      onEventDelete={onEventDelete}
      allowAdd={handleAllowAdd}
      parentId={articleId}
      parentType="article"
      form={form}
    />
  );
};

const ArticleForm = ({
  initialEventDisciplines,
  initialValues = {},
  availableLanguages,
  isArticleCreate,
  articleId,
  saveDraft,
  error,
  form,
}) => {
  const [formValues, watchFormValues] = useWatchFormValues(FORM_VALUES_TO_WATCH, initialValues);
  const [, setEventColorId] = useEventColorsAtom();
  const LOCevent = useLOCevent();
  const { locale } = useParams();

  const [eventDisciplines, setEventDisciplines] = React.useState(initialEventDisciplines);
  const [checkedEvent, setCheckedEvent] = React.useState(initialValues.event);
  const [showLanguageSelects, setShowLanguageSelects] = React.useState(false);
  const [translateError, setTranslateError] = React.useState(null);
  const [eventId, setEventId] = React.useState(LOCevent?.id);
  const [eventLoader, setEventLoader] = useState(false);
  const [isGated, setIsGated] = useState(false);

  const navigateToLanguage = useNavigate();
  const selectLanguageReloadContent = React.useCallback(
    (language) => {
      const location = window.location.pathname;
      const path = `/${location.split('/')[1]}/${location.split('/')[2]}/${language}`;
      navigateToLanguage(path);
    },
    [navigateToLanguage],
  );

  React.useEffect(() => {
    form.resetFields();
  }, [initialValues.language]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const _showLanguageSelects = !!(formValues.type && formValues.columns && formValues.event && checkedEvent);

    setShowLanguageSelects(_showLanguageSelects);
    setEventId(formValues.event?.id);
  }, [checkedEvent, formValues, setEventId, setShowLanguageSelects]);

  const onEventChange = (event, type) => {
    if (!event) return fetchLinkedEvent({ id: null, type });
    const { id } = event;
    fetchLinkedEvent({ id, type });

    if (type === 'linkedEvent') {
      form.setFieldsValue({ [FORM_FIELD.EVENT.name]: event });
      // we discussed that there can be only one related event for article
      form.setFieldsValue({ [FORM_FIELD.RELATED_EVENTS.name]: [event] });
    }
  };

  const handleValuesChange = React.useCallback(
    (change, allChanges) => {
      if (FORM_FIELD.EVENT.name in change) {
        const _eventId = change[FORM_FIELD.EVENT.name]?.id;

        setEventColorId(_eventId);
      }

      if (FORM_FIELD.GATED_CONTENT.name in change) {
        setIsGated(change[FORM_FIELD.GATED_CONTENT.name]);
      }

      if (change.language && initialValues.language) {
        // reload page based on language being changed in the dropdown
        selectLanguageReloadContent(change.language);
      }

      watchFormValues(change, allChanges);
    },
    [form, setEventId, watchFormValues, setEventColorId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchRelatedCompetition = React.useCallback(
    async ({ subCategoryNameUrlSlug: urlSlug }) => {
      const result = await api.competitions.getList({ urlSlug });
      const competition = result.data.length ? result.data[0] : null;
      if (!competition) return;

      const relatedCompetitions = form.getFieldValue(FORM_FIELD.RELATED_COMPETITIONS.name) || [];

      let newRelatedCompetitions = [...relatedCompetitions, competition];
      if (relatedCompetitions.length && relatedCompetitions?.filter((el) => el.id === competition?.id).length)
        newRelatedCompetitions = [...relatedCompetitions];

      form.setFieldsValue({ [FORM_FIELD.RELATED_COMPETITIONS.name]: newRelatedCompetitions });
    },
    [form],
  );

  const fetchLinkedEvent = React.useCallback(
    async ({ id }) => {
      setEventLoader(true);

      const relatedEvents = form.getFieldValue(FORM_FIELD.RELATED_EVENTS.name)?.map((i) => i.id) || [];
      const linkedEvent = [form.getFieldValue(FORM_FIELD.EVENT.name)?.id] || [];

      const allRelatedEvents = [...relatedEvents, ...linkedEvent]?.filter((e) => e !== id).filter(Boolean);
      const linkedEvents = [...allRelatedEvents, id && id].filter(Boolean);

      const result = id ? await api.events.getById({ id }) : {};
      const discRes = await api.disciplines.getChampionshipsDisciplinesByIds({
        ids: linkedEvents,
      });

      const disciplines = transformEventChampionships(discRes);

      setEventLoader(false);
      setEventDisciplines(disciplines || null);

      const { subCategoryNameUrlSlug, categoryCode } = result;

      if (subCategoryNameUrlSlug && categoryCode) {
        fetchRelatedCompetition({ subCategoryNameUrlSlug, categoryCode });
      }
    },
    [form, fetchRelatedCompetition],
  );

  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onValuesChange={handleValuesChange}>
      <ErrorMessage error={error || translateError} variant="block" spaced />

      <MainBlock
        selectLanguageReloadContent={selectLanguageReloadContent}
        showLanguageSelects={showLanguageSelects}
        setEventDisciplines={setEventDisciplines}
        availableLanguages={availableLanguages}
        setTranslateError={setTranslateError}
        isArticleCreate={isArticleCreate}
        setCheckedEvent={setCheckedEvent}
        setEventLoader={setEventLoader}
        onEventChange={onEventChange}
        initialValues={initialValues}
        eventId={eventId}
        locale={locale}
        form={form}
      />

      <SeoBlock initialValues={initialValues} isGated={isGated} />

      <RelatedContentBlock
        eventDisciplines={eventDisciplines}
        isEventFetching={eventLoader}
        onEventChange={onEventChange}
        form={form}
      />

      <ContentModuleBlock form={form} articleId={articleId} saveDraft={saveDraft} />
    </Form>
  );
};

ArticleForm.useForm = () => Form.useForm(FORM_FIELDS, DEFAULT_VALUES);

export default ArticleForm;
